import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckoutButton } from '@/components/payment/checkout-button';
import { PaymentForm } from '@/components/payment/payment-form';
// Componentes de debug removidos
import { ShoppingCart, CreditCard, AlertCircle, ArrowLeft } from 'lucide-react';
import { useCart } from '@/contexts/CartContext';
import { useUser } from '@/contexts/UserContext';

export default function CheckoutPage() {
  const [, setLocation] = useLocation();
  const { cart, clearCart, isLoading: cartLoading, refreshCart } = useCart();
  const { user } = useUser();
  const [checkoutMode, setCheckoutMode] = useState<'session' | 'payment'>('session');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      console.log('❌ [CheckoutPage] useEffect - usuário não logado, redirecionando para login');
      setLocation('/login');
      return;
    }

    // Forçar refresh do carrinho se estiver null
    if (!cart && !cartLoading) {
      refreshCart();
      return;
    }

    // Aguardar o carrinho ser carregado
    if (cartLoading) {

      return;
    }

    if (!cart || cart.items.length === 0) {

      setLocation('/cart');
      return;
    }


  }, [user, cart, cartLoading, setLocation, refreshCart]);

  const handleCheckoutSuccess = (sessionId: string, orderId: string) => {
    console.log('Checkout iniciado:', { sessionId, orderId });
    // O redirecionamento será feito automaticamente pelo Stripe
  };

  const handleCheckoutError = (error: string) => {
    setError(error);
  };

  const handlePaymentSuccess = (paymentIntentId: string) => {
    console.log('Pagamento processado:', paymentIntentId);
    clearCart();
    setLocation('/payment/success');
  };

  const handlePaymentError = (error: string) => {
    setError(error);
  };

  const getTotalAmount = () => {
    if (!cart) return 0;
    return cart.items.reduce((total: number, item: any) => total + (item.unitPrice * item.quantity), 0);
  };

  const getTotalItems = () => {
    if (!cart) return 0;
    return cart.items.reduce((total: number, item: any) => total + item.quantity, 0);
  };

  // Mostrar loading enquanto carrega o carrinho
  if (cartLoading) {
    return (
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p>Carregando carrinho...</p>
        </div>
      </div>
    );
  }

  if (!user || !cart || cart.items.length === 0) {
    return null;
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      {/* Debug components removidos */}

      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => setLocation('/cart')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Voltar ao Carrinho
        </Button>

        <h1 className="text-3xl font-bold">Finalizar Compra</h1>
        <p className="text-primary mt-2">
          Revise seus itens e escolha a forma de pagamento
        </p>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Resumo do Pedido */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ShoppingCart className="h-5 w-5" />
                Resumo do Pedido
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {cart.items.map((item: any) => (
                  <div key={item.id} className="flex items-center gap-4">
                    {/* Thumbnail da foto */}
                    <div className="flex-shrink-0">
                      <img
                        src={item.photo?.storageurl}
                        alt={`Foto do evento ${item.photo?.event?.title || 'Evento'}`}
                        className="w-16 h-16 object-cover rounded"
                      />
                    </div>

                    {/* Detalhes do item */}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">
                        {item.photo?.event?.title || 'Evento'}
                      </p>
                      <p className="text-sm text-primary">
                        {item.photo?.original_filename || `Foto ${item.photoId?.slice(-8)}`}
                      </p>
                      <p className="text-sm text-primary">
                        Quantidade: {item.quantity}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="font-medium">
                        R$ {(item.unitPrice * item.quantity).toFixed(2)}
                      </p>
                      <p className="text-sm text-primary">
                        R$ {item.unitPrice.toFixed(2)} cada
                      </p>
                    </div>
                  </div>
                ))}

                <Separator />

                <div className="flex justify-between items-center text-lg font-semibold">
                  <span>Total ({getTotalItems()} itens)</span>
                  <span>R$ {getTotalAmount().toFixed(2)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Informações do Cliente */}
          <Card>
            <CardHeader>
              <CardTitle>Informações do Cliente</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Nome:</strong> {user.name || 'Não informado'}</p>
                <p><strong>Email:</strong> {user.email}</p>
                <p><strong>ID:</strong> {user.id}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Forma de Pagamento */}
        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Forma de Pagamento
              </CardTitle>
              <CardDescription>
                Escolha como deseja pagar
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex gap-2">
                  <Button
                    variant={checkoutMode === 'session' ? 'default' : 'outline'}
                    onClick={() => setCheckoutMode('session')}
                    className="flex-1"
                  >
                    Checkout Stripe
                  </Button>
                  <Button
                    variant={checkoutMode === 'payment' ? 'default' : 'outline'}
                    onClick={() => setCheckoutMode('payment')}
                    className="flex-1"
                  >
                    Pagamento Direto
                  </Button>
                </div>

                {checkoutMode === 'session' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/10 rounded-lg">
                      <h4 className="font-medium text-foreground mb-2">
                        Checkout Stripe (Recomendado)
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Redirecionamento seguro para o Stripe com suporte a PIX, cartão e outros métodos.
                      </p>
                    </div>

                    <CheckoutButton
                      cartItems={cart.items.map((item: any) => ({
                        photoId: item.photoId,
                        quantity: item.quantity,
                        unitPrice: item.unitPrice,
                        data: item.data, // Incluir dados do evento e foto
                      }))}
                      userId={user.id}
                      successUrl={`${window.location.origin}/payment/success`}
                      cancelUrl={`${window.location.origin}/checkout`}
                      onSuccess={handleCheckoutSuccess}
                      onError={handleCheckoutError}
                      className="w-full"
                    />
                  </div>
                )}

                {checkoutMode === 'payment' && (
                  <div className="space-y-4">
                    <div className="p-4 bg-primary/10 rounded-lg">
                      <h4 className="font-medium text-foreground mb-2">
                        Pagamento Direto
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        Processamento direto com PaymentIntent (requer ID do pedido).
                      </p>
                    </div>

                    <PaymentForm
                      onSuccess={handlePaymentSuccess}
                      onError={handlePaymentError}
                      defaultAmount={getTotalAmount()}
                      className="border-0 shadow-none"
                    />
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Informações de Segurança */}
          <Card>
            <CardHeader>
              <CardTitle className="text-sm">Segurança</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <Badge variant="secondary">SSL</Badge>
                <Badge variant="secondary">Stripe</Badge>
                <Badge variant="secondary">PCI DSS</Badge>
                <span>Pagamento 100% seguro</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}